import {Component, inject} from '@angular/core';
import {MatButton} from "@angular/material/button";
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from "@angular/material/dialog";
import {SummaryAccuracy, SummaryCreateDTO, SummaryLength, SummaryTone} from '../../dto/summary';
import {
  ResearchPaperListComponent
} from '../../../research/components/research-paper-list/research-paper-list.component';
import {MatStepperModule} from '@angular/material/stepper';
import {STEPPER_GLOBAL_OPTIONS} from '@angular/cdk/stepper';
import {MatSliderModule} from '@angular/material/slider';
import {FormsModule} from '@angular/forms';
import {ResearchPaperDTO} from '../../../research/dto/research-paper';
import {Store} from '@ngrx/store';
import {generateProjectSummary} from '../../services/summary/summary.actions';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatIconModule} from '@angular/material/icon';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {CUSTOM_FILTER} from '../../../research/models/filter';

interface CreateSummaryDialogData {
  projectId: number;
}

interface SelectionModel {
  enum: SummaryTone | SummaryLength | SummaryAccuracy,
  label: string,
  description: string
  hint?: string
}

@Component({
  selector: 'app-create-summary-dialog',
  imports: [
    MatButton,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    ResearchPaperListComponent,
    MatStepperModule,
    MatSliderModule,
    FormsModule,
    MatButtonToggleModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './create-summary-dialog.component.html',
  styleUrl: './create-summary-dialog.component.scss',
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: {showError: true},
    },
  ],
})
export class CreateSummaryDialogComponent {
  private readonly store: Store = inject(Store);
  private readonly dialogRef: MatDialogRef<CreateSummaryDialogComponent> = inject(MatDialogRef);
  readonly data: CreateSummaryDialogData = inject<CreateSummaryDialogData>(MAT_DIALOG_DATA);

  papersFilter = [
    CUSTOM_FILTER.TITLE_ABSTRACT_SCREENING_PASSED,
    CUSTOM_FILTER.FULL_TEXT_SCREENING_PASSED,
    CUSTOM_FILTER.MANUAL_OVERWRITTEN,
  ]
  fixedAppliedFilters = [CUSTOM_FILTER.PDF_DOWNLOADED]

  availableTones: SelectionModel[] = [
    {
      enum: SummaryTone.FRIENDLY,
      label: 'Friendly',
      description: 'A friendly tone means that the summary will be written in a friendly and informal way.'
    },
    {
      enum: SummaryTone.NORMAL,
      label: 'Normal',
      description: 'A normal tone means there will be no writing style applied.'
    },
    {
      enum: SummaryTone.SCIENTIFIC,
      label: 'Scientific',
      description: 'A scientific tone means that the summary will be written in a scientific and formal way.'
    }
  ]

  availableLength: SelectionModel[] = [
    {
      enum: SummaryLength.SHORT,
      label: 'Short',
      description: 'A short tone means that the summary will be written in a short and concise way.'
    },
    {
      enum: SummaryLength.MEDIUM,
      label: 'Medium',
      description: 'A medium tone means that the summary will be written in a medium length.'
    },
    {
      enum: SummaryLength.LONG,
      label: 'Long',
      description: 'A long tone means that the summary will be written in a long and detailed way.'
    }
  ]

  availableAccuracy: SelectionModel[] = [
    {
      enum: SummaryAccuracy.SHORT_PARAGRAPH,
      label: 'Short Sentence',
      description: 'A short sentence means that the summary will be written in a short and concise way.'
    },
    {
      enum: SummaryAccuracy.LONG_PARAGRAPH,
      label: 'Long Paragraph',
      description: 'A long paragraph means that the summary will be written in a long and detailed way.'
    },
    {
      enum: SummaryAccuracy.OVERVIEW,
      label: 'Overview',
      description: 'An overview means that the summary will be written in a short and concise way.'
    }
  ]
  papersPageSelected: boolean = false;
  introPageSelected: boolean = false;

  name: string = '';
  selectedPapers: ResearchPaperDTO[] = [];
  selectedTone: SummaryTone = SummaryTone.SCIENTIFIC;
  selectedLength: SummaryLength = SummaryLength.MEDIUM;
  selectedAccuracy: SummaryAccuracy = SummaryAccuracy.OVERVIEW;

  close(): void {
    this.dialogRef.close();
  }

  getLabelTone(): string | undefined {
    return this.availableTones.find(t => t.enum === this.selectedTone)?.description;
  }

  getLabelLength(): string | undefined {
    return this.availableLength.find(t => t.enum === this.selectedLength)?.description;
  }

  getLabelAccuracy(): string | undefined {
    return this.availableAccuracy.find(t => t.enum === this.selectedAccuracy)?.description;
  }

  createSummary(): void {
    if (this.hasError()) {
      return;
    }
    const summary: SummaryCreateDTO = {
      selectedPaperIds: this.selectedPapers.map(paper => paper.id),
      selectedTone: this.selectedTone,
      selectedLength: this.selectedLength,
      selectedAccuracy: this.selectedAccuracy,
      name: this.name,
    }

    this.store.dispatch(generateProjectSummary({
      projectId: this.data.projectId,
      summary: summary
    }));
    this.close();
  }

  selectionChange(index: number): void {
    if (index === 1) {
      this.introPageSelected = true;
    }
    if (index === 2) {
      this.papersPageSelected = true;
    }
  }

  hasErrorPapers(): boolean {
    return this.selectedPapers.length === 0;
  }

  hasErrorName(): boolean {
    return this.name.length === 0;
  }

  hasError(): boolean {
    return this.hasErrorPapers() && this.hasErrorName();
  }
}
